import * as React from "react";

import { useEventConfigs } from "@/shared/state/visualization/hooks";

import { type Timer } from "../../../timer";
import { useWorkspaceEvents } from "../../../WorkspaceCtx/useWorkspaceEvents";

import { ActiveEventPills } from "./ActiveEventPills";
import { selectVisibleEvents } from "./eventFilters";

interface ImagePanelEventsProps {
  timer: Timer;
}

/**
 * Toolbar element that surfaces the workspace's events active at the current
 * playhead time. Every visible event is a candidate, regardless of which signal
 * it is attached to — the same events the workspace timeline shows.
 *
 * Picking the visible events here (independent of the playhead) lets the
 * per-tick timer subscription live in {@link ActiveEventPills}, which is
 * rendered only when there are events to show. So an event-free workspace never
 * subscribes to the timer.
 */
export function ImagePanelEvents({ timer }: ImagePanelEventsProps) {
  const eventsQuery = useWorkspaceEvents();
  const eventConfigs = useEventConfigs();

  const visibleEvents = React.useMemo(
    () => selectVisibleEvents(eventsQuery.data ?? [], eventConfigs),
    [eventsQuery.data, eventConfigs],
  );

  if (visibleEvents.length === 0) {
    return null;
  }

  return <ActiveEventPills events={visibleEvents} timer={timer} />;
}
